import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Building2, Plus } from "lucide-react";
import { Link } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { ApiError } from "@/lib/api/client";
import {
  type ClinicCreateRequest,
  createClinic,
  listClinics,
} from "@/lib/api/admin";
import { formatDate, formatDateTime } from "@/lib/format";

const STATUS_COLOR: Record<string, string> = {
  trial: "yellow",
  active: "green",
  overdue: "orange",
  blocked: "red",
  cancelled: "gray",
};

const STATUS_LABEL: Record<string, string> = {
  trial: "Sinov",
  active: "Faol",
  overdue: "Muddati o'tgan",
  blocked: "Bloklangan",
  cancelled: "Bekor qilingan",
};

export function AdminClinicsPage() {
  const queryClient = useQueryClient();
  const [createOpen, createHandlers] = useDisclosure(false);

  const { data: clinics, isLoading } = useQuery({
    queryKey: ["admin-clinics"],
    queryFn: listClinics,
  });

  const form = useForm<ClinicCreateRequest>({
    initialValues: {
      name: "",
      legal_name: "",
      license_number: "",
      address: "",
      phone: "",
      inn: "",
      card_prefix: "CLI",
      admin_login: "",
      admin_password: "",
      admin_full_name: "",
      plan_name: "basic",
      monthly_fee: 500000,
      currency: "UZS",
      trial_days: 14,
    },
    validate: {
      name: (v) => (v.trim().length > 0 ? null : "Klinika nomi shart"),
      admin_login: (v) =>
        v.trim().length >= 2 ? null : "Login kamida 2 belgi",
      admin_password: (v) =>
        (v ?? "").length >= 6 ? null : "Parol kamida 6 belgi",
      admin_full_name: (v) =>
        v.trim().length > 0 ? null : "Admin F.I.SH shart",
    },
  });

  const createMutation = useMutation({
    mutationFn: createClinic,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["admin-clinics"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      notifications.show({
        color: "green",
        message: `Klinika yaratildi: ${created.name}`,
      });
      form.reset();
      createHandlers.close();
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : "Yaratib bo'lmadi";
      notifications.show({ color: "red", message: msg });
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Klinikalar"
        subtitle={clinics ? `${clinics.length} ta klinika` : undefined}
        back
        actions={
          <Button
            leftSection={<Plus size={16} strokeWidth={2.4} />}
            radius="xl"
            onClick={createHandlers.open}
          >
            Yangi klinika
          </Button>
        }
      />

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={1100}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Klinika</Table.Th>
                <Table.Th>Holat</Table.Th>
                <Table.Th ta="right">Xodimlar</Table.Th>
                <Table.Th ta="right">Bemorlar</Table.Th>
                <Table.Th ta="right">Ko'riklar</Table.Th>
                <Table.Th ta="right">Yakunlangan</Table.Th>
                <Table.Th>Oxirgi faollik</Table.Th>
                <Table.Th>Yaratilgan</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Text c="dimmed" ta="center" py="md">Yuklanmoqda…</Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {!isLoading && (clinics ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <EmptyState
                      icon={<Building2 size={32} strokeWidth={1.5} />}
                      title="Hali klinika yo'q"
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {(clinics ?? []).map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={700} size="sm">{c.name}</Text>
                      {c.legal_name && (
                        <Text size="xs" c="dimmed">{c.legal_name}</Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    {c.billing_status ? (
                      <Badge
                        variant="light"
                        color={STATUS_COLOR[c.billing_status] ?? "gray"}
                        radius="sm"
                      >
                        {STATUS_LABEL[c.billing_status] ?? c.billing_status}
                      </Badge>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={600}>{c.user_count}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={600}>{c.patient_count}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={600}>{c.examination_count}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={600} c="green.7">{c.completed_exam_count}</Text></Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {c.last_exam_at ? formatDateTime(c.last_exam_at) : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{formatDate(c.created_at)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      radius="xl"
                      component={Link}
                      to={`/admin/clinics/${c.id}`}
                      rightSection={<ArrowRight size={14} strokeWidth={2.4} />}
                    >
                      Batafsil
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal
        opened={createOpen}
        onClose={createHandlers.close}
        title="Yangi klinika qo'shish"
        size="lg"
        radius="lg"
      >
        <form onSubmit={form.onSubmit((v) => createMutation.mutate(v))}>
          <Stack>
            <Text size="xs" fw={700} tt="uppercase" c="brand.7">
              Klinika ma'lumotlari
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput label="Nomi" required {...form.getInputProps("name")} />
              <TextInput label="Yuridik nomi" {...form.getInputProps("legal_name")} />
              <TextInput label="Litsenziya №" {...form.getInputProps("license_number")} />
              <TextInput label="STIR (INN)" {...form.getInputProps("inn")} />
              <TextInput label="Manzil" {...form.getInputProps("address")} />
              <TextInput label="Telefon" {...form.getInputProps("phone")} />
              <TextInput
                label="Karta prefiksi"
                placeholder="CLI"
                {...form.getInputProps("card_prefix")}
              />
            </SimpleGrid>

            <Text size="xs" fw={700} tt="uppercase" c="brand.7" mt="sm">
              Birinchi admin foydalanuvchi
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput label="Login" required {...form.getInputProps("admin_login")} />
              <TextInput
                label="Parol (kamida 6 belgi)"
                required
                {...form.getInputProps("admin_password")}
              />
              <TextInput
                label="F.I.SH"
                required
                {...form.getInputProps("admin_full_name")}
                style={{ gridColumn: "1 / -1" }}
              />
            </SimpleGrid>

            <Text size="xs" fw={700} tt="uppercase" c="brand.7" mt="sm">
              To'lov rejasi
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <Select
                label="Reja"
                data={[
                  { value: "basic", label: "Basic" },
                  { value: "pro", label: "Pro" },
                  { value: "enterprise", label: "Enterprise" },
                ]}
                {...form.getInputProps("plan_name")}
              />
              <NumberInput
                label="Oylik to'lov"
                thousandSeparator=" "
                min={0}
                {...form.getInputProps("monthly_fee")}
              />
              <NumberInput
                label="Sinov muddati (kun)"
                min={0}
                max={365}
                {...form.getInputProps("trial_days")}
              />
            </SimpleGrid>
            <Text size="xs" c="dimmed">
              Sinov muddati tugaganda klinika avtomatik bloklanadi. To'lov
              qilingandan keyin admin paneldan qayta faollashtiriladi.
            </Text>

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" color="gray" onClick={createHandlers.close}>
                Bekor qilish
              </Button>
              <Button type="submit" loading={createMutation.isPending} radius="xl">
                Yaratish
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
